import React, { useState } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { Star, Zap, Circle, Users, MapPin, CalendarClock, Phone, MessageCircle, Pencil, Check, X, ShieldCheck, Sparkles } from 'lucide-react';
import { Negociacao, Profile } from '../../../types';
import { supabase } from '../../../src/lib/supabase';
import SlaTimer from '../../../src/components/SlaTimer';
import { proximaAcaoPadrao, scorePrioridade, proximaVisita, diasDesdeCriacao, diasSemInteracao, pendenciaDaAnalise, ScorePrioridade } from './kanbanWork';
import DefinirTermoValidoModal from '../components/NegotiationDetails/DefinirTermoValidoModal';
import { isRemota, modalidadeLabel } from '../../../src/lib/modalidade';
import { useAuth } from '../../../src/contexts/AuthContext';
import { logUsageEvent } from '../../../src/lib/logger';

// Mini confetti animation for approved terms
const confettiKeyframes = `
@keyframes confetti-fall-1 { 0% { transform: translate(0, 0) rotate(0deg); opacity: 1; } 100% { transform: translate(-8px, -14px) rotate(200deg); opacity: 0; } }
@keyframes confetti-fall-2 { 0% { transform: translate(0, 0) rotate(0deg); opacity: 1; } 100% { transform: translate(6px, -16px) rotate(-180deg); opacity: 0; } }
@keyframes confetti-fall-3 { 0% { transform: translate(0, 0) rotate(0deg); opacity: 1; } 100% { transform: translate(-10px, -10px) rotate(150deg); opacity: 0; } }
@keyframes confetti-fall-4 { 0% { transform: translate(0, 0) rotate(0deg); opacity: 1; } 100% { transform: translate(10px, -12px) rotate(-220deg); opacity: 0; } }
@keyframes confetti-fall-5 { 0% { transform: translate(0, 0) rotate(0deg); opacity: 1; } 100% { transform: translate(-4px, -18px) rotate(260deg); opacity: 0; } }
@keyframes confetti-fall-6 { 0% { transform: translate(0, 0) rotate(0deg); opacity: 1; } 100% { transform: translate(8px, -8px) rotate(-140deg); opacity: 0; } }
@keyframes confetti-fall-7 { 0% { transform: translate(0, 0) rotate(0deg); opacity: 1; } 100% { transform: translate(2px, -20px) rotate(300deg); opacity: 0; } }
@keyframes confetti-fall-8 { 0% { transform: translate(0, 0) rotate(0deg); opacity: 1; } 100% { transform: translate(-6px, -6px) rotate(-260deg); opacity: 0; } }
`;

const MiniConfetti: React.FC = () => {
    const particles = [
        { color: '#FFD700', size: 4, left: '20%', top: '50%', anim: 'confetti-fall-1', delay: '0s', dur: '1.8s' },
        { color: '#FF6B6B', size: 3, left: '40%', top: '40%', anim: 'confetti-fall-2', delay: '0.2s', dur: '2s' },
        { color: '#4ECDC4', size: 3, left: '60%', top: '60%', anim: 'confetti-fall-3', delay: '0.1s', dur: '1.6s' },
        { color: '#45B7D1', size: 4, left: '80%', top: '30%', anim: 'confetti-fall-4', delay: '0.3s', dur: '2.2s' },
        { color: '#96CEB4', size: 3, left: '30%', top: '70%', anim: 'confetti-fall-5', delay: '0.15s', dur: '1.9s' },
        { color: '#FFEAA7', size: 4, left: '70%', top: '50%', anim: 'confetti-fall-6', delay: '0.25s', dur: '1.7s' },
        { color: '#DDA0DD', size: 3, left: '50%', top: '30%', anim: 'confetti-fall-7', delay: '0.05s', dur: '2.1s' },
        { color: '#FF8C42', size: 4, left: '10%', top: '60%', anim: 'confetti-fall-8', delay: '0.35s', dur: '1.5s' },
    ];

    return (
        <>
            <style>{confettiKeyframes}</style>
            <span style={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none' }}>
                {particles.map((p, i) => (
                    <span
                        key={i}
                        style={{
                            position: 'absolute',
                            left: p.left,
                            top: p.top,
                            width: p.size,
                            height: p.size,
                            backgroundColor: p.color,
                            borderRadius: i % 2 === 0 ? '50%' : '1px',
                            animation: `${p.anim} ${p.dur} ${p.delay} infinite ease-out`,
                        }}
                    />
                ))}
            </span>
        </>
    );
};

interface KanbanCardProps {
    negociacao: Negociacao;
    index: number;
    onOpenDetails: (negociacao: Negociacao) => void;
    onCreateVisita: (negociacao: Negociacao) => void;
    profiles: Profile[];
    isLeanView?: boolean;
    isAdmin?: boolean;
    onChanged?: () => void;
    /** Tem aviso de "lead atribuído" ainda não visto (nunca abriu o detalhe) — liga o halo pulsante. */
    naoAberto?: boolean;
}

const SCORE_STYLE: Record<ScorePrioridade, string> = {
    P1: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    P2: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    P3: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};

const KanbanCard: React.FC<KanbanCardProps> = React.memo(({ negociacao, index, onOpenDetails, onCreateVisita, profiles, isLeanView, isAdmin, onChanged, naoAberto }) => {
    const { user } = useAuth();
    // Local state for optimistic updates
    const [rating, setRating] = useState(negociacao.avaliacao_estrelas);
    const [favorito, setFavorito] = useState(!!negociacao.favorito);
    const [scoreOverride, setScoreOverride] = useState<ScorePrioridade | null>(negociacao.score_prioridade || null);
    const [proximaAcaoOverride, setProximaAcaoOverride] = useState<string | null>(negociacao.proxima_acao ?? null);
    const [editingAcao, setEditingAcao] = useState(false);
    const [acaoDraft, setAcaoDraft] = useState('');
    const [showTermoAdmin, setShowTermoAdmin] = useState(false);

    // Admin: correção de "duplo envio de termo". Aparece só p/ admin quando há 2+ termos
    // e a negociação ainda não foi aprovada/reprovada (i.e., pode estar presa antes da análise).
    const numTermos = negociacao.url_termo?.length || 0;
    const showTermoAdminBtn = !!isAdmin && numTermos >= 2 &&
        !['termo_aprovado', 'termo_reprovado'].includes(negociacao.status);

    // Score: usa override local quando há; senão calcula o automático (ignorando o override do prop)
    const score = scoreOverride || scorePrioridade({ ...negociacao, score_prioridade: null } as Negociacao);
    const acaoView = { ...negociacao, proxima_acao: proximaAcaoOverride };
    const proximaAcao = proximaAcaoPadrao(acaoView as Negociacao);
    // Já analisada e devolvida ao vendedor — não confundir com a fila de análise.
    const pendencia = pendenciaDaAnalise(negociacao);
    const visita = proximaVisita(negociacao);
    const whatsappDigits = (negociacao.whatsapp || '').replace(/\D/g, '');

    const handleToggleFavorito = async (e: React.MouseEvent) => {
        e.stopPropagation();
        const novo = !favorito;
        setFavorito(novo); // optimistic
        try {
            await supabase.from('negociacoes').update({ favorito: novo }).eq('id', negociacao.id);
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'favoritar_negociacao', origem: 'acao' });
        } catch (err) {
            console.error('Erro ao favoritar:', err);
            setFavorito(!novo);
        }
    };

    // Cicla o score: auto → P1 → P2 → P3 → auto
    const handleCycleScore = async (e: React.MouseEvent) => {
        e.stopPropagation();
        const ordem: Array<ScorePrioridade | null> = ['P1', 'P2', 'P3', null];
        const idx = ordem.indexOf(scoreOverride);
        const proximo = ordem[(idx + 1) % ordem.length];
        setScoreOverride(proximo); // optimistic
        try {
            await supabase.from('negociacoes').update({ score_prioridade: proximo }).eq('id', negociacao.id);
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'mudar_prioridade_negociacao', origem: 'acao' });
        } catch (err) {
            console.error('Erro ao alterar prioridade:', err);
            setScoreOverride(scoreOverride);
        }
    };

    const startEditAcao = (e: React.MouseEvent) => {
        e.stopPropagation();
        setAcaoDraft(proximaAcao);
        setEditingAcao(true);
    };

    const saveAcao = async (e?: React.MouseEvent) => {
        e?.stopPropagation();
        const val = acaoDraft.trim();
        setProximaAcaoOverride(val || null); // optimistic; vazio = volta ao padrão
        setEditingAcao(false);
        try {
            await supabase.from('negociacoes').update({ proxima_acao: val || null }).eq('id', negociacao.id);
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'editar_proxima_acao', origem: 'acao' });
        } catch (err) {
            console.error('Erro ao salvar próxima ação:', err);
        }
    };



    // Helper for status badge color
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'em_andamento': return 'bg-blue-100 text-blue-700';
            case 'ganho': return 'bg-green-100 text-green-700';
            case 'perdido': return 'bg-red-100 text-red-700';
            case 'em_pausa': return 'bg-yellow-100 text-yellow-700';
            case 'simulacao_realizada': return 'bg-purple-100 text-purple-700';
            case 'termo_enviado': return 'bg-yellow-100 text-yellow-700';
            case 'termo_assinado': return 'bg-teal-100 text-teal-700';
            case 'termo_analise': return 'bg-orange-100 text-orange-700';
            case 'termo_aprovado': return 'bg-green-100 text-green-700';
            case 'termo_reprovado': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const getStatusLabel = (status: string) => {
        // Transform snake_case to readable text if needed, or mapping
        const map: Record<string, string> = {
            'em_andamento': 'Em andamento',
            'ganho': 'Ganho',
            'perdido': 'Perdido',
            'em_pausa': 'Em pausa',
            'simulacao_realizada': 'Simulação realizada',
            'termo_enviado': 'Termo enviado',
            'termo_assinado': 'Termo assinado',
            'termo_analise': 'Termo em análise',
            'termo_aprovado': 'Termo aprovado',
            'termo_reprovado': 'Termo reprovado'
        };
        return map[status] || status;
    };

    // Calculate "cooldown"
    const getDaysSinceUpdate = () => {
        const updated = new Date(negociacao.updated_at).getTime();
        const now = new Date().getTime();
        const diff = Math.floor((now - updated) / (1000 * 60 * 60 * 24));
        return diff;
    };

    const daysSinceUpdate = getDaysSinceUpdate();

    // Handle Star Click
    const handleStarClick = async (newRating: number) => {
        setRating(newRating); // Optimistic
        try {
            await supabase
                .from('negociacoes')
                .update({ avaliacao_estrelas: newRating })
                .eq('id', negociacao.id);
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'avaliar_negociacao', origem: 'acao' });
        } catch (error) {
            console.error('Error updating stars:', error);
            setRating(negociacao.avaliacao_estrelas); // Revert
        }
    };

    // Task Indicator Logic
    const getTaskIndicator = () => {
        if (!negociacao.tarefas || negociacao.tarefas.length === 0) return null;

        const tasks = negociacao.tarefas;
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        // Filter active tasks
        const activeTasks = tasks.filter(t => !['concluido', 'cancelado'].includes(t.status));

        if (activeTasks.length === 0) return null;

        let hasOverdue = false;
        let hasExpiringSoon = false;

        activeTasks.forEach(t => {
            // Lê YYYY-MM-DD manualmente para evitar que ISO converta "hoje 00:00" em UTC -> "ontem" localmente (UTC-3).
            const dateParts = t.vencimento.split('T')[0].split('-');
            const taskDate = new Date(
                parseInt(dateParts[0]),
                parseInt(dateParts[1]) - 1,
                parseInt(dateParts[2])
            );

            const diffDays = Math.round((taskDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

            if (diffDays < 0) {
                hasOverdue = true;
            } else if (diffDays <= 2) {
                hasExpiringSoon = true;
            }
        });

        if (hasOverdue) {
            return (
                <div className="relative flex items-center justify-center w-4 h-4" title="Existem tarefas atrasadas">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </div>
            );
        } else if (hasExpiringSoon) {
            return (
                <div className="relative flex items-center justify-center w-4 h-4" title="Tarefas vencendo em breve">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-500"></span>
                </div>
            );
        } else {
            return (
                <div className="relative flex items-center justify-center w-4 h-4" title="Tarefas em dia">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </div>
            );
        }
    };

    return (
        <Draggable draggableId={String(negociacao.id)} index={index}>
            {(provided, snapshot) => (

                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={`rounded-xl shadow-sm border group transition-all mb-3 ${negociacao.status === 'termo_reprovado'
                        ? 'bg-red-50/50 dark:bg-red-950/20 border-red-200 dark:border-red-900/50 hover:shadow-md hover:border-red-300 dark:hover:border-red-800/80'
                        : negociacao.status === 'termo_analise'
                            ? 'bg-orange-50/50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-900/50 hover:shadow-md hover:border-orange-300 dark:hover:border-orange-800/80'
                            : naoAberto
                                ? 'bg-white dark:bg-gray-800 border-primary/40 dark:border-primary/40 hover:shadow-md'
                                : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 hover:shadow-md hover:border-primary/30 dark:hover:border-primary/30'
                        } ${naoAberto ? 'animate-pulse-glow motion-reduce:animate-none' : ''} ${snapshot.isDragging ? 'rotate-2 shadow-xl ring-2 ring-primary ring-opacity-50 z-50' : ''} ${isLeanView ? 'p-3' : 'p-4'}`}
                    style={{ ...provided.draggableProps.style }}
                >
                    {/* Header Tags */}
                    <div className={`flex flex-wrap gap-2 items-center justify-between ${isLeanView ? 'mb-2' : 'mb-3'}`}>
                        <div className="flex gap-2 items-center">
                            <span className={`px-2 py-1 rounded text-[10px] uppercase font-bold tracking-wide ${pendencia ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-300' : getStatusColor(negociacao.status)}`} style={{ position: 'relative' }}>
                                {negociacao.status === 'termo_aprovado' && <MiniConfetti />}
                                {pendencia ?? getStatusLabel(negociacao.status)}
                            </span>
                            {/* Task Indicator */}
                            {!isLeanView && getTaskIndicator()}
                        </div>

                        <div className="flex items-center gap-1.5">
                            {/* Selo Lemit em 2 níveis: só a empresa/pessoa vs. também o CPF do dono */}
                            {(negociacao as any)._lemitEnriquecido && (
                                (negociacao as any)._lemitDono ? (
                                    <span
                                        title="Empresa e CPF do dono consultados na Lemit"
                                        className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-extrabold tracking-wide bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                    >
                                        <Sparkles size={11} /> Lemit + dono
                                    </span>
                                ) : (
                                    <span
                                        title="Empresa consultada na Lemit — falta consultar o CPF do dono"
                                        className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-extrabold tracking-wide bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400"
                                    >
                                        <Sparkles size={11} /> Lemit
                                    </span>
                                )
                            )}
                            {/* Admin: corrigir duplo envio de termo */}
                            {showTermoAdminBtn && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowTermoAdmin(true); }}
                                    title={`Admin: definir qual dos ${numTermos} termos foi assinado e enviar para análise`}
                                    className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-extrabold tracking-wide bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors"
                                >
                                    <ShieldCheck size={12} />
                                    {numTermos} termos
                                </button>
                            )}
                            {daysSinceUpdate > 7 && !isLeanView && (
                                <span className="px-2 py-1 rounded text-[10px] uppercase font-bold tracking-wide bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                                    Esfriando há {daysSinceUpdate} dias
                                </span>
                            )}
                            {/* Score de prioridade P1/P2/P3 (clique cicla; sobrescreve o automático) */}
                            <button
                                onClick={handleCycleScore}
                                title={scoreOverride ? `Prioridade ${score} (manual) — clique para alterar` : `Prioridade ${score} (automática) — clique para fixar`}
                                className={`px-1.5 py-0.5 rounded text-[10px] font-extrabold tracking-wide transition-transform hover:scale-105 ${SCORE_STYLE[score]} ${scoreOverride ? 'ring-1 ring-current/30' : ''}`}
                            >
                                {score}
                            </button>
                            {/* Favorito ("Prioridade do dia") — estrela separada da qualificação */}
                            <button
                                onClick={handleToggleFavorito}
                                title={favorito ? 'Remover dos favoritos' : 'Marcar como favorito (Prioridade do dia)'}
                                className="focus:outline-none transition-transform hover:scale-110 active:scale-90"
                            >
                                <Star size={15} fill={favorito ? 'currentColor' : 'none'} className={favorito ? 'text-amber-400' : 'text-gray-300 dark:text-gray-600'} />
                            </button>
                        </div>
                    </div>

                    {/* Main Info */}
                    <div
                        className={`${isLeanView ? 'mb-2' : 'mb-3'} cursor-pointer`}
                        onClick={() => onOpenDetails(negociacao)}
                    >
                        <h4 className="font-bold text-gray-800 dark:text-white text-sm leading-tight mb-1 hover:text-primary transition-colors truncate">{negociacao.titulo}</h4>
                        {!isLeanView && (
                            <>
                                {negociacao.cliente_nome !== negociacao.titulo && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{negociacao.cliente_nome}</p>
                                )}
                                {(() => {
                                    const ucs = negociacao.unidades_consumidoras;
                                    const cidade = ucs?.find(u => u.cidade)?.cidade;
                                    let display = `UC: ${negociacao.numero_uc || 'N/A'}`;

                                    if (ucs && ucs.length > 0) {
                                        const numbers = ucs.map(u => u.numero_uc);
                                        const label = ucs.length > 1 ? 'UCs' : 'UC';
                                        if (numbers.length <= 3) {
                                            display = `${label}: ${numbers.join(', ')}`;
                                        } else {
                                            const firstThree = numbers.slice(0, 3).join(', ');
                                            display = `${label}: ${firstThree}, [+]...`;
                                        }
                                    }
                                    return (
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="text-xs text-gray-400 dark:text-gray-500 font-mono leading-tight truncate">{display}</p>
                                            {cidade && (
                                                <span className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap flex-shrink-0">
                                                    <MapPin size={9} className="text-gray-300 dark:text-gray-600" />
                                                    {cidade}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })()}
                            </>
                        )}
                        {/* Lean view: city inline */}
                        {isLeanView && (() => {
                            const ucs = negociacao.unidades_consumidoras;
                            const cidade = ucs?.find(u => u.cidade)?.cidade;
                            if (!cidade) return null;
                            return (
                                <span className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500 truncate">
                                    <MapPin size={9} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
                                    {cidade}
                                </span>
                            );
                        })()}
                    </div>

                    {/* Datas no cabeçalho (doc 01 §3.4) */}
                    {!isLeanView && (
                        <p className="text-[10px] text-gray-400 dark:text-gray-500 mb-2 -mt-1">
                            Criado há {diasDesdeCriacao(negociacao)}d · Última interação há {diasSemInteracao(negociacao)}d
                        </p>
                    )}

                    {/* Linha "Próxima Ação" (doc 01 §3.1) — derivada do status, editável */}
                    {!isLeanView && (
                        editingAcao ? (
                            <div className="flex items-center gap-1 mb-2" onClick={e => e.stopPropagation()}>
                                <input
                                    autoFocus
                                    value={acaoDraft}
                                    onChange={e => setAcaoDraft(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') saveAcao(); if (e.key === 'Escape') setEditingAcao(false); }}
                                    className="flex-1 min-w-0 px-2 py-1 text-[11px] rounded-md bg-gray-50 dark:bg-gray-700/60 border border-primary/40 text-gray-700 dark:text-gray-200 focus:outline-none"
                                    placeholder="Próxima ação..."
                                />
                                <button onClick={saveAcao} className="p-1 rounded text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"><Check size={13} /></button>
                                <button onClick={(e) => { e.stopPropagation(); setEditingAcao(false); }} className="p-1 rounded text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={13} /></button>
                            </div>
                        ) : (
                            <div className="group/acao flex items-center gap-1.5 mb-2 text-[11px]">
                                <span className="text-gray-400 dark:text-gray-500">Próxima ação:</span>
                                <span className="font-semibold text-gray-600 dark:text-gray-300 truncate">{proximaAcao}</span>
                                <button onClick={startEditAcao} className="opacity-0 group-hover/acao:opacity-100 transition-opacity p-0.5 rounded text-gray-400 hover:text-primary" title="Editar próxima ação">
                                    <Pencil size={11} />
                                </button>
                            </div>
                        )
                    )}

                    {/* Reflexo da Rota: próxima visita agendada (doc 01 §3.6) */}
                    {visita && (
                        <div className={`flex items-center gap-1.5 text-[11px] font-semibold text-primary mb-2 ${isLeanView ? 'mt-1' : ''}`}>
                            {isRemota(visita.modalidade) ? <Phone size={12} /> : <MapPin size={12} />}
                            <span>Próxima visita: {new Date(visita.data + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} · {modalidadeLabel(visita.modalidade)}</span>
                        </div>
                    )}

                    {/* Metadata & Values */}
                    <div className={`flex flex-col gap-3 ${isLeanView ? 'border-t-0 pt-0' : 'border-t border-gray-50 dark:border-gray-700 pt-3'}`}>
                        {!isLeanView && (
                            <div className="flex justify-between items-end">
                                <div className="flex items-center gap-1">
                                    {/* Star Rating */}
                                    <div className="flex">
                                        {[...Array(5)].map((_, i) => (
                                            <button
                                                key={i}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStarClick(i + 1);
                                                }}
                                                className="focus:outline-none transition-transform hover:scale-110 active:scale-90"
                                            >
                                                <Star
                                                    size={14}
                                                    fill={i < rating ? "currentColor" : "none"}
                                                    className={i < rating ? "text-yellow-400" : "text-gray-300 dark:text-gray-600"}
                                                />
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="text-right">
                                    <div className="flex items-center justify-end gap-1 text-sm font-bold text-gray-700 dark:text-gray-200">
                                        <Zap size={14} className="text-yellow-500 fill-yellow-500" />
                                        {negociacao.consumo_medio_kwh?.toLocaleString('pt-BR')} kWh
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* User Creator Profile */}
                        {(() => {
                            const creatorId = negociacao.user_id || negociacao.responsavel_id;
                            const creator = profiles.find(p => p.id === creatorId);
                            const roleDotColor = creator?.role === 'vendedor'
                                ? 'bg-blue-500 dark:bg-blue-400'
                                : (creator?.role === 'parceiro' || creator?.role === 'vendedor_parceiro')
                                    ? 'bg-teal-500 dark:bg-teal-400'
                                    : null;

                            return (
                        <div className={`flex items-center gap-2 mt-auto pb-0 ${isLeanView ? 'justify-between pt-0' : 'pt-2'}`}>
                            <div className="flex items-center gap-2">
                                {(() => {
                                    if (!creator) return (
                                        <>
                                            <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-500 dark:text-gray-400">
                                                ?
                                            </div>
                                            <span className="text-[11px] text-gray-400 dark:text-gray-500">Sem responsável</span>
                                        </>
                                    );

                                    const name = creator.full_name || 'Desconhecido';
                                    const initials = name.charAt(0).toUpperCase();

                                    return (
                                        <>
                                            {!isLeanView && (
                                                creator.avatar_url ? (
                                                    <img src={creator.avatar_url} alt={name} className="w-6 h-6 rounded-full object-cover border border-gray-200 dark:border-gray-600" />
                                                ) : (
                                                    <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-500 dark:text-gray-400">
                                                        {initials}
                                                    </div>
                                                )
                                            )}
                                            <div className="flex flex-col justify-center">
                                                <span className={`text-[11px] font-medium text-gray-600 dark:text-gray-300 truncate max-w-[120px] leading-tight ${isLeanView ? 'font-bold' : ''}`}>
                                                    {name}
                                                </span>
                                                {!isLeanView && creator.role === 'vendedor_parceiro' && creator.parceiro_id && (() => {
                                                    const linkedTo = profiles.find(p => p.id === creator.parceiro_id);
                                                    const isGerente = linkedTo?.role === 'gerente_parcerias';
                                                    return (
                                                        <div className={`flex items-center gap-1 text-[9px] mt-0.5 ${isGerente ? 'text-purple-600 dark:text-purple-400' : 'text-indigo-600 dark:text-indigo-400'}`} title={isGerente ? 'Vinculado ao Ger. Parcerias' : 'Vinculado ao Parceiro'}>
                                                            <Users size={10} />
                                                            <span className="truncate max-w-[100px] font-semibold">
                                                                {isGerente ? 'Ger. Parcerias: ' : ''}{linkedTo?.full_name?.split(' ')[0] || 'Desconhecido'}
                                                            </span>
                                                        </div>
                                                    );
                                                })()}
                                                {!isLeanView && creator.role === 'parceiro' && (
                                                    <div className="flex items-center gap-1 text-[9px] text-teal-600 dark:text-teal-400 mt-0.5" title="Equipe Parceiro">
                                                        <Users size={10} />
                                                        <span className="font-semibold">Parceiro</span>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>

                            {roleDotColor && (
                                <span
                                    className={`ml-auto w-2 h-2 rounded-full shrink-0 ${roleDotColor}`}
                                    title={creator?.role === 'vendedor' ? 'Vendedor' : 'Parceiro'}
                                />
                            )}

                            {isLeanView && (
                                <div className="text-right">
                                    <div className="flex items-center justify-end gap-1 text-[11px] font-bold text-gray-700 dark:text-gray-200">
                                        <Zap size={11} className="text-yellow-500 fill-yellow-500" />
                                        {negociacao.consumo_medio_kwh?.toLocaleString('pt-BR')} kWh
                                    </div>
                                </div>
                            )}
                        </div>
                            );
                        })()}
                    </div>

                    {/* Footer Actions (Hover only) — doc 01 §3.5: Criar visita · Ligar · WhatsApp */}
                    {!isLeanView && (
                        <div className="mt-3 pt-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1 flex-wrap">
                            <button
                                onClick={(e) => { e.stopPropagation(); onCreateVisita(negociacao); }}
                                className="text-[11px] font-semibold text-gray-500 hover:text-primary dark:text-gray-400 dark:hover:text-primary flex items-center gap-1 px-2.5 py-1 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                title="Criar visita"
                            >
                                <CalendarClock size={12} /> Criar visita
                            </button>
                            {whatsappDigits && (
                                <>
                                    <a
                                        href={`tel:+55${whatsappDigits}`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-[11px] font-semibold text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 flex items-center gap-1 px-2.5 py-1 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        title="Ligar"
                                    >
                                        <Phone size={12} /> Ligar
                                    </a>
                                    <a
                                        href={`https://wa.me/55${whatsappDigits}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-[11px] font-semibold text-gray-500 hover:text-emerald-600 dark:text-gray-400 dark:hover:text-emerald-400 flex items-center gap-1 px-2.5 py-1 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        title="WhatsApp"
                                    >
                                        <MessageCircle size={12} /> WhatsApp
                                    </a>
                                </>
                            )}
                        </div>
                    )}

                    {showTermoAdmin && (
                        <div onClick={(e) => e.stopPropagation()}>
                            <DefinirTermoValidoModal
                                isOpen={showTermoAdmin}
                                onClose={() => setShowTermoAdmin(false)}
                                negociacao={negociacao}
                                onSuccess={() => { onChanged?.(); }}
                            />
                        </div>
                    )}
                </div>
            )}
        </Draggable>
    );
});

export default KanbanCard;
